import type { ScanPhoto, ScanPhotoTimeline } from '@kaiizen/kai-scan-template-system';

/**
 * Where the photo view is pointed, and how it moves.
 *
 * The view is the grid with one cell blown up, so it navigates in the grid's own terms: a row, a
 * column, and which photo of that cell is on screen. Everything here is pure and works off the
 * timeline the panel already computed — the view never re-derives the grid, it reads the same one.
 */

/** Where a cell sits in the grid. The grid, its scrolling and its moves all speak in these. */
export type ProgressAltCellCoordinates = {
  rowIndex: number;
  columnIndex: number;
};

/** A cell of the timeline plus which of its photos is showing. */
export type ProgressAltCursor = ProgressAltCellCoordinates & {
  photoId: string;
};

/** Which way a move goes. Rows run oldest-first, so `later` is down the grid and `earlier` is up. */
export type ProgressAltCursorDirection = 'left' | 'right' | 'earlier' | 'later';

/** Names a row across a change of grouping: an instant, or the one bucket that has no instant. */
export type ProgressAltRowAt = number | 'undated';

/** Names a column without depending on its position, which changes as the scan is re-scoped. */
export type ProgressAltColumnRef = { groupId: string; taskId?: string };

/**
 * Which bucket of a column, and which of that cell's photos — the part of an address that has no
 * route slot to live in, so it rides in the query string. See `progressAltCellUrl`.
 */
export type ProgressAltCellAddress = {
  at?: ProgressAltRowAt;
  photoId?: string;
};

/** What the URL asks for. Any of it may be stale, so nothing here is trusted without checking. */
export type ProgressAltCursorRequest = ProgressAltColumnRef & ProgressAltCellAddress;

const EMPTY_PHOTOS: readonly ScanPhoto[] = [];

export const getCellPhotos = (
  timeline: ScanPhotoTimeline,
  rowIndex: number,
  columnIndex: number,
): readonly ScanPhoto[] => timeline.rows[rowIndex]?.cells[columnIndex]?.photos ?? EMPTY_PHOTOS;

const hasPhotos = (timeline: ScanPhotoTimeline, rowIndex: number, columnIndex: number) => getCellPhotos(timeline, rowIndex, columnIndex).length > 0;

const getRowAt = (timeline: ScanPhotoTimeline, rowIndex: number): ProgressAltRowAt => timeline.rows[rowIndex]?.periodStart ?? 'undated';

const getColumnRef = (
  timeline: ScanPhotoTimeline,
  columnIndex: number,
): ProgressAltColumnRef | undefined => {
  const column = timeline.columns[columnIndex];
  return column ? { groupId: column.groupId, taskId: column.taskId } : undefined;
};

/**
 * Names a cursor without leaning on its position: the column by its ids, the row by the instant it
 * opens. What both the URL and the grid's reveal target are written in, because either may be read back
 * against a timeline that has since been regrouped or re-scoped.
 *
 * Nothing when the column is gone, which is the one part that cannot be named without it.
 */
export const getCursorRequest = (
  timeline: ScanPhotoTimeline,
  { rowIndex, columnIndex, photoId }: ProgressAltCursor,
): ProgressAltCursorRequest | undefined => {
  const columnRef = getColumnRef(timeline, columnIndex);
  return columnRef ? { ...columnRef, at: getRowAt(timeline, rowIndex), photoId } : undefined;
};

const findColumnIndex = (timeline: ScanPhotoTimeline, { groupId, taskId }: ProgressAltColumnRef) =>
  timeline.columns.findIndex(column => column.groupId === groupId && column.taskId === taskId);

const findRowIndex = (timeline: ScanPhotoTimeline, at: ProgressAltRowAt) => timeline.rows.findIndex(row => (row.periodStart ?? 'undated') === at);

/** Where the grid should be looking. A row only when the address names one that still exists. */
export type ProgressAltRevealTarget = {
  columnIndex: number;
  rowIndex?: number;
};

/**
 * Which cell of the grid an address points at, for scrolling rather than for opening.
 *
 * Weaker than `resolveCursor` on purpose: a column the reader selected is worth scrolling to whether
 * or not anything was ever photographed in it, so this asks only that the column exist. The row is
 * whatever bucket the address names, and is simply left out when it names none — which is what makes
 * one horizontal move out of a plain change of scope, and a move on both axes out of a returning
 * reader who was looking at a particular bucket.
 */
export const findRevealTarget = (
  timeline: ScanPhotoTimeline,
  columnRef: ProgressAltColumnRef,
  { at }: ProgressAltCellAddress = {},
): ProgressAltRevealTarget | undefined => {
  const columnIndex = findColumnIndex(timeline, columnRef);
  if (columnIndex === -1) return undefined;
  const rowIndex = at == null ? -1 : findRowIndex(timeline, at);
  return { columnIndex, ...(rowIndex === -1 ? {} : { rowIndex }) };
};

/**
 * Walks a column from `fromRowIndex` inclusive in one direction and answers with the first bucket
 * holding photos, or nothing once it runs off the end.
 *
 * The one way this file looks along a column — asking whether a scope was ever photographed, stepping
 * through time, and falling back when a link goes stale are all this same walk from a different start.
 * A start already outside the grid simply finds nothing, which is what lets callers step off the edge
 * without checking first.
 */
const findRowWithPhotos = (
  timeline: ScanPhotoTimeline,
  columnIndex: number,
  fromRowIndex: number,
  step: -1 | 1,
): number | undefined => {
  for (let rowIndex = fromRowIndex; rowIndex >= 0 && rowIndex < timeline.rows.length; rowIndex += step) {
    if (hasPhotos(timeline, rowIndex, columnIndex)) return rowIndex;
  }
  return undefined;
};

/**
 * Which bucket of a column to open, coming from `fromRowIndex`.
 *
 * The same bucket when it holds photos, so moving across a walk stays on that walk. Failing that the
 * nearest earlier one, because a scope photographed before the current bucket is progress already made
 * and reads as the state of things; only when there is nothing behind does it reach forward.
 *
 * Between them the two walks cover every row, so this answers nothing precisely when the column was
 * never photographed — which is what lets `findScopeCell` ask one question instead of two.
 */
const findNearestRowWithPhotos = (
  timeline: ScanPhotoTimeline,
  columnIndex: number,
  fromRowIndex: number,
): number | undefined =>
  findRowWithPhotos(timeline, columnIndex, fromRowIndex, -1)
    ?? findRowWithPhotos(timeline, columnIndex, fromRowIndex + 1, 1);

/**
 * The next scope along that was ever photographed, and which of its buckets to open.
 *
 * Availability is a question about the whole column, not about the current row. A reader stepping
 * through the scopes of a renovation means "show me the next thing that was worked on" — skipping a
 * task because its photos happen to fall on a different day would hide work that plainly exists, and
 * leave the arrow dead for no reason the reader can see. Asking for the nearest bucket already asks
 * exactly that, so a column that answers nothing is a column that was never photographed.
 */
const findScopeCell = (
  timeline: ScanPhotoTimeline,
  { rowIndex, columnIndex }: ProgressAltCellCoordinates,
  step: -1 | 1,
): ProgressAltCellCoordinates | undefined => {
  for (let next = columnIndex + step; next >= 0 && next < timeline.columns.length; next += step) {
    const row = findNearestRowWithPhotos(timeline, next, rowIndex);
    if (row != null) return { rowIndex: row, columnIndex: next };
  }
  return undefined;
};

/**
 * The next bucket of this same scope, or nothing if there is none.
 *
 * Empty buckets are passed over rather than stopped at: an empty cell has nothing to show, so stopping
 * on one would make the arrow appear to do nothing. The search runs to the edge of the grid, so a scope
 * photographed on day 1 and day 30 is one press apart, not thirty.
 */
const findTimeCell = (
  timeline: ScanPhotoTimeline,
  { rowIndex, columnIndex }: ProgressAltCellCoordinates,
  step: -1 | 1,
): ProgressAltCellCoordinates | undefined => {
  const next = findRowWithPhotos(timeline, columnIndex, rowIndex + step, step);
  return next == null ? undefined : { rowIndex: next, columnIndex };
};

/**
 * The cell the reader would land on moving one step, or nothing if there is none.
 *
 * The two axes ask different questions, so they answer differently. Across, the reader is choosing a
 * scope and time follows; down, the reader is choosing a time and the scope holds still.
 */
const findNeighborCell = (
  timeline: ScanPhotoTimeline,
  cursor: ProgressAltCellCoordinates,
  direction: ProgressAltCursorDirection,
): ProgressAltCellCoordinates | undefined =>
  direction === 'left' || direction === 'right'
    ? findScopeCell(timeline, cursor, direction === 'left' ? -1 : 1)
    : findTimeCell(timeline, cursor, direction === 'earlier' ? -1 : 1);

/**
 * The next `limit` cells in one direction, nearest first, stopping at the edge of the grid.
 *
 * What the time navigator needs to show a stretch of the timeline rather than a single step: each cell
 * is found from the one before it, so empty buckets are passed over exactly as a run of single steps
 * would, and the list is short precisely when the timeline is.
 */
export const findNeighborCells = (
  timeline: ScanPhotoTimeline,
  cursor: ProgressAltCellCoordinates,
  direction: ProgressAltCursorDirection,
  limit: number,
): ProgressAltCellCoordinates[] => {
  const cells: ProgressAltCellCoordinates[] = [];
  let from = cursor;
  while (cells.length < limit) {
    const next = findNeighborCell(timeline, from, direction);
    if (!next) break;
    cells.push(next);
    from = next;
  }
  return cells;
};

/** Opens a cell on its newest photo, matching the tile the grid was showing for it. */
export const getCursorAtCell = (
  timeline: ScanPhotoTimeline,
  { rowIndex, columnIndex }: ProgressAltCellCoordinates,
): ProgressAltCursor | undefined => {
  const photoId = getCellPhotos(timeline, rowIndex, columnIndex).at(-1)?.photoId;
  return photoId ? { rowIndex, columnIndex, photoId } : undefined;
};

/** Which bucket of a column holds a given photo, or nothing when the column no longer has it. */
const findRowWithPhoto = (
  timeline: ScanPhotoTimeline,
  columnIndex: number,
  photoId: string,
): number | undefined => {
  for (let rowIndex = timeline.rows.length - 1; rowIndex >= 0; rowIndex -= 1) {
    if (getCellPhotos(timeline, rowIndex, columnIndex).some(photo => photo.photoId === photoId)) return rowIndex;
  }
  return undefined;
};

/**
 * Turns what the URL asked for into a cell that actually holds photos, or nothing.
 *
 * A URL outlives the grid it was written against — regroup by week, re-scope to one group, delete the
 * photo — so every part of the request is checked rather than trusted. A named cell that has emptied
 * out falls back to its column's nearest photo-bearing bucket, which keeps a stale link landing
 * somewhere recognisable instead of on an error.
 *
 * An address may also name a photo and no bucket at all — a link from a photo strip, whose author has
 * no idea how the panel is bucketing time. The photo is then what locates the bucket, so such a link
 * opens the photo it names rather than merely the scope it belongs to. A named-but-stale bucket is a
 * different case and keeps its own fallback: the reader was last looking at a bucket, so the nearest
 * surviving one is the more faithful place to put them back.
 */
export const resolveCursor = (
  timeline: ScanPhotoTimeline,
  request: ProgressAltCursorRequest,
): ProgressAltCursor | undefined => {
  const columnIndex = findColumnIndex(timeline, request);
  if (columnIndex === -1) return undefined;

  const requestedRowIndex = request.at == null ? -1 : findRowIndex(timeline, request.at);
  if (requestedRowIndex !== -1 && hasPhotos(timeline, requestedRowIndex, columnIndex)) {
    const photos = getCellPhotos(timeline, requestedRowIndex, columnIndex);
    const photoId = photos.some(photo => photo.photoId === request.photoId)
      ? request.photoId!
      : photos.at(-1)!.photoId;
    return { rowIndex: requestedRowIndex, columnIndex, photoId };
  }

  // No bucket named at all, but a photo was: let the photo say which bucket to open.
  if (request.at == null && request.photoId) {
    const photoRowIndex = findRowWithPhoto(timeline, columnIndex, request.photoId);
    if (photoRowIndex != null) return { rowIndex: photoRowIndex, columnIndex, photoId: request.photoId };
  }

  // The named bucket is gone or has emptied: fall to the column's newest bucket that still has photos.
  const fallbackRowIndex = findRowWithPhotos(timeline, columnIndex, timeline.rows.length - 1, -1);
  return fallbackRowIndex == null
    ? undefined
    : getCursorAtCell(timeline, { rowIndex: fallbackRowIndex, columnIndex });
};
